import { Component, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { TrackingService } from '../../core/shared/tracking.service';
import { ToastService } from '../../core/shared/toast.service';
import { UtilsService } from '../../core/shared/utils.service';
import { environment } from '../../../environments/environment';
import { FormioTranslation } from '../../core/interfaces/forms/formio-translation';
import { ItemFile } from '../../core/interfaces/items/item-file';
import { ItemWorkflow } from '../../core/interfaces/items/item-workflow';
import { TrackingFieldDisplay } from '../../core/interfaces/items/item-tracking-field';
import { TrackingWorkflowStep } from '../../core/interfaces/items/item-workflow-step';
import { texts } from '../../core/shared/translations-formio';

import * as Formio from 'formiojs';

@Component({
	selector: 'app-tracking-detail',
	templateUrl: './tracking-detail.component.html',
	styleUrls: ['./tracking-detail.component.scss']
})
export class TrackingDetailComponent implements OnInit, OnDestroy {

	private readonly SUBSCRIPTIONS: Subscription = new Subscription();

	private formID: any = '';
	private trackingId: string = '';

	public loading: boolean = true;
	public notFound: boolean = false;
	public config: any = {};
	public logo: string = '';
	public fields: TrackingFieldDisplay[] = [];
	public itemFiles: Pick<ItemFile, 'name' | 'url'>[] = [];
	public workflowSteps: TrackingWorkflowStep[] = [];

	public uploadFormSchema: any;
	public uploadSubmission: any = { data: {} };
	public refreshUploadForm = new EventEmitter();
	public uploadSuccess: boolean = false;
	public uploading: boolean = false;
	private fileFieldKey: string = '';

	public renderOptions: {
		i18n: FormioTranslation;
		language: string;
		noAlerts: boolean;
	} = {
			i18n: texts,
			language: 'es',
			noAlerts: false
		};

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private trackingService: TrackingService,
		private spinner: NgxSpinnerService,
		private toastService: ToastService,
		private utils: UtilsService
	) { }

	ngOnInit(): void {
		this.SUBSCRIPTIONS.add(this.route.paramMap.subscribe((params: ParamMap) => {
			this.loadTrackingDetail(params);
		}));
	}

	private async loadTrackingDetail(params: ParamMap): Promise<void> {
		this.loading = true;
		this.notFound = false;
		this.fields = [];

		this.spinner.show();

		this.formID = params.get('formID');
		this.trackingId = params.get('trackingId') ?? '';

		try {
			if (!this.formID || !this.trackingId) {
				await this.router.navigateByUrl('not-found');
				return;
			}

			const [configResponse, searchResponse] = await Promise.allSettled([
				this.trackingService.getTrackingConfig(this.formID),
				this.trackingService.searchItem(this.formID, this.trackingId)
			]);

			if (configResponse.status === 'fulfilled' && configResponse.value?.data) {
				this.config = configResponse.value.data;
				this.logo = `${environment.imagesUrl}${this.config.FormLogo}`;
				document.title = this.config.PageTitle;

				if (this.config.WSThemes?.primary)
            		document.documentElement.style.setProperty('--ws-color', this.config.WSThemes.primary.toString());
				if (this.config.WSID)
					window.localStorage.setItem('currentWorkspace', this.config.WSID);
				if (this.config.FormID)
					window.localStorage.setItem('currentSchemaId', this.config.FormID.toString());
			}

			if (searchResponse.status === 'rejected') {
				const error = searchResponse.reason;
				if (error?.status === 404) {
					this.notFound = true;
				} else {
					this.toastService.danger('Error al consultar el expediente');
					console.error('Error searching item: ', error);
				}
			} else if (searchResponse.status === 'fulfilled') {
				const item = searchResponse.value?.data?.item;
				const workflowAuditLog = searchResponse.value?.data?.workflowAuditLog ?? [];
				const stepsById = searchResponse.value?.data?.stepsById ?? {};
				if (item) {
					this.fields = this.buildTrackingFieldDisplay(item, this.config.FormSchema, this.config.ViewConfig);
					this.itemFiles = this.buildItemFiles(item);
					this.workflowSteps = this.buildWorkflowSteps(stepsById, item?.Data?.Workflow, workflowAuditLog);
					this.buildUploadFormSchema();
				} else {
					this.notFound = true;
				}
			}

			this.renderOptions = await this.utils.renderFormioOptions(this.renderOptions);

		} catch (error: any) {
			this.toastService.danger('Error al cargar los datos');
			console.error('Error in tracking detail: ', error);
		} finally {
			this.spinner.hide();
			this.loading = false;
		}
	}

	ngOnDestroy(): void {
		this.SUBSCRIPTIONS.unsubscribe();
	}

	/**
	 * Navigates back to the tracking search page
	 */
	public goBack(): void {
		this.router.navigate(['tracking', this.formID]);
	}

	/**
	 * Handles the Formio upload form submission
	 * @param submission - Formio submission object containing the uploaded files data
	 */
	public async onUploadSubmit(submission: any): Promise<void> {
		this.uploading = true;
		this.spinner.show();

		try {
			const files = submission.data[this.fileFieldKey] ?? [];

			if (!files.length) {
				this.toastService.danger('No se han seleccionado archivos');
				return;
			}

			await this.trackingService.uploadTrackingFiles(
				this.formID,
				this.trackingId,
				files
			);

			this.uploadSuccess = true;
		} catch (error: any) {
			console.error('Error uploading files:', error);
			this.toastService.danger('Error al subir los archivos');
			this.refreshUploadForm.emit({ form: this.uploadFormSchema });
		} finally {
			this.uploading = false;
			this.spinner.hide();
		}
	}

	/**
	 * Builds the Formio form schema for the file upload section
	 */
	private buildUploadFormSchema(): void {
		this.fileFieldKey = this.config.FileFieldKey ?? '';

		if (!this.fileFieldKey) {
			this.uploadFormSchema = null;
			return;
		}

		this.uploadFormSchema = {
			display: 'form',
			components: [
				{
					type: 'file',
					key: this.fileFieldKey,
					label: '',
					storage: 'FirebaseStorage',
					multiple: true,
					input: true
				},
				{
					type: 'button',
					key: 'submit',
					label: 'Enviar archivos',
					action: 'submit',
					disableOnInvalid: true,
					theme: 'primary'
				}
			]
		};
	}

	/**
	 * Builds an ordered list of {label, value} pairs from the item data,
	 * using FormSchema component definitions to resolve labels and option labels.
	 * @param item - The item object from the API response
	 * @param formSchemaJson - JSON string of the Formio form schema
	 * @param viewConfig - Optional map of field keys to display labels
	 */
	private buildTrackingFieldDisplay(item: any, formSchemaJson: string, viewConfig?: Record<string, string> | null): TrackingFieldDisplay[] {
		const records: any = item?.Data?.Records ?? {};
		const componentMap: Record<string, any> = {};

		if (formSchemaJson) {
			try {
				const formSchema = typeof formSchemaJson === 'string' ? JSON.parse(formSchemaJson) : formSchemaJson;
				Formio.Utils.eachComponent(formSchema.components ?? [], (component: any) => {
					if (component.key) {
						componentMap[component.key] = component;
					}
				});
			} catch (e) {
				console.warn('Could not parse FormSchema for labels', e);
			}
		}

		const isFileField: (key: string) => boolean = (key: string) =>
			componentMap[key]?.type === 'file';

		if (viewConfig && Object.keys(viewConfig).length) {
			return Object.entries(viewConfig)
				.filter(([key]) => !isFileField(key) && records[key] !== null && records[key] !== undefined && records[key] !== '')
				.map(([key, label]) => ({
					label,
					value: this.formatValue(records[key], componentMap[key])
				}));
		}

		return Object.keys(records)
			.filter(key => !key.startsWith('_') && !isFileField(key) && records[key] !== null && records[key] !== undefined && records[key] !== '')
			.map(key => ({
				label: componentMap[key]?.label || key,
				value: this.formatValue(records[key], componentMap[key])
			}));
	}

	/**
	 * Builds a flat list of files from item.Data.System.Files
	 * @param item - The item object from the API response
	 */
	private buildItemFiles(item: any): Pick<ItemFile, 'name' | 'url'>[] {
		const systemFiles: any[] = item?.Data?.System?.Files ?? [];
		return systemFiles
			.map((f: any) => ({ name: f.Filename || f.originalName || f.name || '', url: f.Url || f.url }))
			.filter((f: Pick<ItemFile, 'name'>) => f.name);
	}

	/**
	 * Formats a record value for display, resolving option labels, dates, files and arrays
	 * @param value - The raw record value to format
	 * @param component - Optional Formio component definition for resolving labels
	 */
	private formatValue(value: any, component?: any): string {
		if (typeof value === 'boolean') {
			return value ? 'Sí' : 'No';
		}

		if (component?.type === 'file' && Array.isArray(value)) {
			return value.map(f => f.originalName || f.name || '').filter(Boolean).join('\n');
		}

		if (component) {
			const options: any[] = component.data?.values ?? component.values ?? [];
			if (options.length > 0) {
				if (Array.isArray(value)) {
					return value.map(v => options.find((o: any) => String(o.value) === String(v))?.label ?? String(v)).join(', ');
				}
				const match = options.find((o: any) => String(o.value) === String(value));
				if (match) return match.label;
			}
		}

		if (Array.isArray(value)) {
			return value.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
		}
		if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value))) {
			return new Date(value).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
		}
		if (typeof value === 'object') {
			return JSON.stringify(value);
		}
		return String(value);
	}

	/**
	 * Maps the audit log into the TrackingWorkflowStep[] display format.
	 * Mirrors webapp's workflowHistory() dialog: every audit log entry becomes a
	 * timeline step regardless of WFInstance state, so finished/archived workflows
	 * still render their full history. The entry whose stepId matches the current
	 * item.Data.Workflow assignment is rendered as 'active' or 'finalized' (based
	 * on whether the step has outgoing transitions); every other entry is 'done'.
	 * When the audit log has no entries for a workflow, falls back to rendering
	 * just the current step from itemWorkflows so the UI is never empty.
	 * The final .reverse() puts the newest entry on top of the timeline.
	 * @param stepsById - Map of stepId -> step config (Name, Transitions, ...)
	 * @param itemWorkflows - Optional array of workflow assignments from the item
	 * @param auditLog - Audit log entries scoped to WORKFLOW for this item
	 */
	private buildWorkflowSteps(stepsById: Record<string, any>, itemWorkflows?: ItemWorkflow[], auditLog: any[] = []): TrackingWorkflowStep[] {
		const currentStepByWfId = new Map<string, string>();
		for (const wf of itemWorkflows ?? []) {
			if (wf?.WorkflowId != null && wf?.WorkflowStepId != null)
				currentStepByWfId.set(String(wf.WorkflowId), String(wf.WorkflowStepId));
		}

		const allSteps: TrackingWorkflowStep[] = [];

		const sortedEntries = (auditLog ?? [])
			.filter(e => e?.Details && e.Details.workflowStepId != null)
			.map(e => ({
				wfId: e.Details.workflowId != null ? String(e.Details.workflowId) : '',
				stepId: String(e.Details.workflowStepId),
				date: typeof e.Date === 'string' ? e.Date : e.Date?.toString?.() ?? ''
			}))
			.sort((a, b) => a.date.localeCompare(b.date));

		const activeIndexByWfId = new Map<string, number>();
		for (let i = sortedEntries.length - 1; i >= 0; i--) {
			const entry = sortedEntries[i];
			if (activeIndexByWfId.has(entry.wfId)) continue;
			if (currentStepByWfId.get(entry.wfId) === entry.stepId)
				activeIndexByWfId.set(entry.wfId, i);
		}

		const wfIdsRendered = new Set<string>();
		for (let i = 0; i < sortedEntries.length; i++) {
			const entry = sortedEntries[i];
			const stepConfig = stepsById[entry.stepId];
			const stepName = stepConfig?.Name ?? '';
			if (!stepName) continue;
			let status: 'done' | 'active' | 'finalized' = 'done';
			if (activeIndexByWfId.get(entry.wfId) === i)
				status = this.hasOutgoingTransition(stepConfig) ? 'active' : 'finalized';
			wfIdsRendered.add(entry.wfId);
			allSteps.push({
				name: stepName,
				status,
				date: entry.date ? this.formatStepDate(entry.date) : undefined
			});
		}

		for (const wf of itemWorkflows ?? []) {
			const wfId = wf?.WorkflowId != null ? String(wf.WorkflowId) : '';
			const currentStepId = wf?.WorkflowStepId != null ? String(wf.WorkflowStepId) : '';
			if (!wfId || !currentStepId || wfIdsRendered.has(wfId)) continue;
			const stepConfig = stepsById[currentStepId];
			const isTerminal = !this.hasOutgoingTransition(stepConfig);
			allSteps.push({
				name: stepConfig?.Name || wf.WorkflowStepName || '',
				status: isTerminal ? 'finalized' : 'active',
				date: undefined
			});
		}

		return allSteps.reverse();
	}

	/**
	 * Returns true when the step has any outgoing transition (goTo or NextStep).
	 * A step with no outgoing transitions is the workflow's terminal step.
	 * @param step - rawStep entry with Transitions
	 */
	private hasOutgoingTransition(step: any): boolean {
		if (!step?.Transitions) return false;
		let transitions: any[] = [];
		try {
			transitions = typeof step.Transitions === 'string'
				? JSON.parse(step.Transitions)
				: (Array.isArray(step.Transitions) ? step.Transitions : Object.values(step.Transitions));
		} catch {
			return false;
		}
		return transitions.some(t => t?.config?.goTo != null
			|| (Array.isArray(t?.config?.conditions) && t.config.conditions.some((c: any) => c?.NextStep != null)));
	}

	/**
	 * Formats an ISO date string into a localized short date (e.g. "5 mar 2025").
	 * @param isoDate - ISO 8601 date string to format
	 */
	private formatStepDate(isoDate: string): string {
		try {
			return new Date(isoDate).toLocaleDateString('es-ES', {
				day: 'numeric', month: 'short', year: 'numeric'
			});
		} catch {
			return '';
		}
	}
}
